/**
 * Admin portal permission registry.
 * Maps backend permission `type` values (GET /admin/permissions/get-all)
 * to admin nav items, routes, and tabs.
 */

/** Nav keys that are always visible for authenticated admin users. */
export const ADMIN_PUBLIC_NAV_KEYS = ["dashboard", "settings"]

/** Backend permission module keys granted via roles (admin staff only). */
export const ADMIN_PERMISSION_MODULE_KEYS = [
  "invites",
  "roles",
  "permission",
  "collaborator-limits",
  "city-update-requests",
  "cities",
  "role-types",
  "spellbooks",
  "role-classes",
  "staff",
  "calls",
  "blogs",
  "photography",
  "photography-collaborator",
  "flags",
  "projects",
  "rewards",
  "reward-types",
  "reward-claims",
  "review-writer",
  "reviews",
  "sessions",
  "activity-logs",
  "members",
]

/**
 * Nav item → permission modules.
 * Sidebar shows the item only when the user has VIEW on at least one module.
 * Create / edit / delete alone must not reveal the item (listing requires view).
 */
export const ADMIN_NAV_PERMISSION_MODULES = {
  dashboard: null,
  cities: ["cities"],
  projects: ["projects"],
  sessions: ["sessions"],
  members: ["members"],
  rewards: ["rewards", "reward-types", "reward-claims"],
  // Inbox & Calls listing is calls-only — require calls.view (not blogs / not update)
  inbox: ["calls"],
  "arts-genome": ["spellbooks", "role-types", "role-classes"],
  requests: [
    "invites",
    "collaborator-limits",
    "city-update-requests",
    "review-writer",
    "photography-collaborator",
  ],
  flags: ["flags"],
  staff: ["staff"],
  roles: ["roles", "permission"],
  settings: null,
}

/** Admin Requests tabs → permission module. */
export const ADMIN_REQUEST_TAB_PERMISSION_MODULES = {
  collaborator_limits: "collaborator-limits",
  change_city: "city-update-requests",
  join_platform: "invites",
  reviewers: "review-writer",
  photographers: "photography-collaborator",
}

/** Arts Genome tabs → permission module. Keyword mappings are role-types. */
export const ADMIN_ARTS_GENOME_TAB_PERMISSION_MODULES = {
  spellbook: "spellbooks",
  class: "role-classes",
  keyword: "role-types",
}

/** Arts Genome route :type segment → permission module. */
export const ADMIN_ARTS_GENOME_TYPE_PERMISSION_MODULES = {
  spellbook: "spellbooks",
  class: "role-classes",
  keyword: "role-types",
}

/**
 * Nested VIEW modules on the destination screen.
 * Listing View buttons stay hidden unless every one is granted.
 */
export const ADMIN_STAFF_PROFILE_NESTED_VIEW_MODULES = [
  "roles",
  "cities",
  "permission",
  "activity-logs",
]
export const ADMIN_ROLE_DETAIL_NESTED_VIEW_MODULES = ["permission", "members"]

/** Request listing tabs — used by nav + /admin/requests route. */
export const ADMIN_REQUEST_PERMISSION_MODULES = [
  "invites",
  "collaborator-limits",
  "city-update-requests",
  "review-writer",
  "photography-collaborator",
]

/** Dashboard KPI cards → permission module (hide without view). */
export const ADMIN_DASHBOARD_KPI_MODULES = {
  total_members: "members",
  active_cities: "cities",
  active_projects: "projects",
  sessions: "sessions",
  inbox_calls: "calls",
  rewards_xp: "rewards",
}

/** Dashboard “Top Modules Usage” keys → permission module. */
export const ADMIN_DASHBOARD_MODULE_USAGE_PERMISSION = {
  projects: "projects",
  sessions: "sessions",
  inbox_calls: "calls",
  rewards: "rewards",
  photography: "photography",
}

/** City detail KPI `to` keys → permission module. */
export const ADMIN_CITY_DETAIL_KPI_MODULES = {
  members: "members",
  projects: "projects",
  "shadow-working-events": "sessions",
}

/** City detail pending cards → permission module. */
export const ADMIN_CITY_PENDING_PERMISSION_MODULES = {
  reviews: "reviews",
  calls: "calls",
  photography: "photography",
  flags: "flags",
  transfers: "city-update-requests",
}
